import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Button, IconButton, Chip,
  Paper, CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import apiClient from '../services/apiClient';
import { useLanguage } from '../context/LanguageContext';
import BreadcrumbsTrail from './BreadcrumbsTrail';

/**
 * BreadcrumbsPathDetail — path header + the story-progression trail (#2 redesign).
 *
 * The progress card and the three collapsible "In Progress / Not Started /
 * Completed" lists were replaced by <BreadcrumbsTrail>, which renders the
 * ordered spine of story nodes (locked/available/in_progress/complete), the
 * "you are here" marker, percent-complete, badge milestones, and the primary
 * Continue CTA. This component now just provides the page chrome (header,
 * description, tags) and delegates progression to the trail.
 */
const BreadcrumbsPathDetail = () => {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguage();

  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchPath = async () => {
      setLoading(true);
      try {
        // The trail fetches its own (richer) data; here we only need the path
        // header metadata (title, level, description, tags, stories_count).
        const response = await apiClient.breadcrumbs.getPath(pathId, selectedLanguage);
        if (active) setPath(response.data.path);
      } catch (error) {
        console.error('Error fetching path details:', error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchPath();
    return () => { active = false; };
  }, [pathId, selectedLanguage]);

  const handleStartStory = (storyId) => {
    navigate(`/breadcrumbs/stories/${storyId}`);
  };

  const handleBack = () => {
    navigate('/breadcrumbs');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!path) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" paragraph>
          Path not found
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Paths
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky', top: 0, zIndex: 10,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {path.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                label={path.level}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'primary.main', color: 'white' }}
              />
              <Typography variant="caption" color="text.secondary">
                • {path.stories_count} stories
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ p: 2 }}>
        {/* Path Description */}
        {path.description && (
          <Card
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 3, bgcolor: 'background.paper' }}
          >
            <Typography variant="body2" color="text.secondary">
              {path.description}
            </Typography>
          </Card>
        )}

        {/* Story progression trail (#2 redesign) */}
        <BreadcrumbsTrail
          pathId={pathId}
          languageCode={selectedLanguage}
          onSelectStory={handleStartStory}
        />

        {/* Tags */}
        {path.tags && path.tags.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Topics covered:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {path.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BreadcrumbsPathDetail;
